/**
 * 战前简报浮层组件（屏 1）
 *
 * 点击教学关卡卡片时展开，显示：
 * - 叙事文字（从 manifest v5.narrative.briefing）
 * - 对手 Archetype 档案（代号 + 战术风格）
 * - 任务目标
 * - API 参考
 *
 * 设计约束：Ardot v2.0 战术美学（0圆角/描边/monospace）
 */

// Ardot v2.0 战术美学（0圆角/描边/monospace）

export interface BriefingData {
  /** 关卡 ID */
  levelId: string;
  /** 关卡名称 */
  levelName: string;
  /** 阶段名称 */
  phaseName: string;
  /** 战前简报文案 */
  briefing: string;
  /** 任务目标描述 */
  objective: string;
  /** 对手 Archetype */
  archetype?: {
    id: string;
    name: string;
    codename: string;
    tacticalStyle: string;
    cardDescription: string;
  };
  /** 可用 API */
  apiSurface: string[];
  /** 提示密度（星级） */
  hintDensity: number;
  /** 提示文字 */
  hintText: string;
  /** 身份头衔 */
  rankName: string;
}

/** 打开战前简报浮层 */
export function openBriefingModal(data: BriefingData): void {
  closeBriefingModal();

  const backdrop = document.createElement("div");
  backdrop.className = "modal-backdrop briefing-backdrop";
  backdrop.id = "bc-briefing-backdrop";

  const stars = "★".repeat(Math.min(data.hintDensity, 5)) + "☆".repeat(Math.max(0, 5 - data.hintDensity));

  backdrop.innerHTML = `
    <div class="modal briefing-modal" role="dialog" aria-labelledby="briefing-title">
      <!-- 顶部身份标识 -->
      <div class="briefing-header">
        <span class="briefing-rank">${escapeHtml(data.rankName.toUpperCase())}</span>
        <span class="briefing-sep">·</span>
        <span class="briefing-phase">${escapeHtml(data.phaseName.toUpperCase())}</span>
      </div>

      <!-- 标题 -->
      <h2 class="briefing-title" id="briefing-title">${escapeHtml(data.levelName.toUpperCase())}</h2>

      <!-- 叙事文字 -->
      <div class="briefing-narrative">
        <p>${escapeHtml(data.briefing)}</p>
      </div>

      <!-- 任务目标 -->
      <div class="briefing-section">
        <div class="briefing-section-label">▸ 推演目标</div>
        <div class="briefing-section-text">${escapeHtml(data.objective)}</div>
      </div>

      <!-- 对手情报 -->
      ${data.archetype ? `
      <div class="briefing-section">
        <div class="briefing-section-label">▸ 对手情报</div>
        <div class="briefing-archetype">
          <div class="briefing-archetype-row">
            <span class="briefing-archetype-name">${escapeHtml(data.archetype.name)}</span>
            <span class="briefing-archetype-code">${escapeHtml(data.archetype.codename)}</span>
          </div>
          <div class="briefing-archetype-style">${escapeHtml(data.archetype.tacticalStyle)}</div>
        </div>
      </div>
      ` : ""}

      <!-- API 参考 -->
      <div class="briefing-section">
        <div class="briefing-section-label">▸ 可用指令</div>
        <div class="briefing-api-tags">
          ${data.apiSurface.map((api) => `<span class="briefing-api-tag">${escapeHtml(api)}</span>`).join("")}
        </div>
      </div>

      <!-- 提示 -->
      <div class="briefing-section">
        <div class="briefing-section-label">▸ 战术提示 <span class="briefing-hint-stars">${stars}</span></div>
        <div class="briefing-section-text briefing-hint-text">${escapeHtml(data.hintText)}</div>
      </div>

      <!-- 操作按钮 -->
      <div class="briefing-actions">
        <button class="btn btn-ghost" id="bc-briefing-close">关闭</button>
        <button class="btn btn-primary" id="bc-briefing-enter">进入推演 →</button>
      </div>
    </div>
  `;
  document.body.appendChild(backdrop);

  // 事件绑定
  backdrop.querySelector("#bc-briefing-close")?.addEventListener("click", closeBriefingModal);
  backdrop.querySelector("#bc-briefing-enter")?.addEventListener("click", () => {
    const event = new CustomEvent("briefing-enter", { detail: { levelId: data.levelId } });
    document.dispatchEvent(event);
    closeBriefingModal();
  });
  backdrop.addEventListener("click", (e) => {
    if (e.target === backdrop) closeBriefingModal();
  });
  document.addEventListener("keydown", escHandler);
  function escHandler(e: KeyboardEvent) {
    if (e.key === "Escape") {
      closeBriefingModal();
      document.removeEventListener("keydown", escHandler);
    }
  }
}

export function closeBriefingModal(): void {
  document.getElementById("bc-briefing-backdrop")?.remove();
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]!));
}
